import { AppComponent } from './app/app.component';

import { ClaimsEffects } from '@app-store/claims/claims.effects';

import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import 'hammerjs';

import { EffectsModule } from '@ngrx/effects';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { DocumentRef } from '@app-core/reference/document.reference';
import { WindowRef } from '@app-core/reference/window.reference';
import { ApiService, ConfigService } from '@app-core/services';
import { TranslationLoader } from '@app-core/translate-loader/translate.loader';
import { CartEffects } from '@app-store/cart/cart.effects';
import { ConfigEffects } from '@app-store/config/config.effects';
import { HistoryEffects } from '@app-store/history/history.effects';
import { PrismicEffects } from '@app-store/prismic/prismic.effects';
import { ProductEffects } from '@app-store/product/product.effects';
import { reducers } from '@app-store/reducers';
import { RemitEffects } from '@app-store/remit/remit.effects';
import { UserEffects } from '@app-store/user/user.effects';
import { VehicleEffects } from '@app-store/vehicle/vehicle.effects';
import { JwtModule } from '@auth0/angular-jwt';
import { localStorageSync } from 'ngrx-store-localstorage';
import { CookieService } from 'ngx-cookie-service';

import { APP_ROUTES } from './app/app-routes';
import { requestInterceptor } from './app/interceptor/interceptor';

const localStorageSyncReducer = (reducer: ActionReducer<unknown>): ActionReducer<unknown> => {
  return localStorageSync({
    keys: ['userState', 'productState', 'cartState', 'vehicleState'],
    rehydrate: true,
  })(reducer);
};

const metaReducers: MetaReducer[] = [localStorageSyncReducer];

// required for AOT compilation
const HttpLoaderFactory = (http: HttpClient, apiService: ApiService): TranslationLoader =>
  new TranslationLoader(apiService);

const tokenGetter = (): string => localStorage.getItem('token');

const environment = ConfigService.getEnvironment();

if (environment.production) {
  enableProdMode();
} else {
  if (environment?.apiMock?.enabled) {
    // eslint-disable-next-line
    const { worker } = require('./mocks/browser');
    worker.start({
      onUnhandledRequest: environment?.apiMock?.showWarnings ? 'warn' : 'bypass',
    });
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(APP_ROUTES),
    importProvidersFrom(
      BrowserModule,
      FormsModule,
      ReactiveFormsModule,
      // Add this import here
      JwtModule.forRoot({
        config: { tokenGetter },
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient, ApiService],
        },
      }),
      StoreModule.forRoot(reducers, {
        metaReducers,
        runtimeChecks: {
          strictActionImmutability: false,
          strictActionSerializability: false,
          strictActionTypeUniqueness: false,
          strictActionWithinNgZone: false,
          strictStateImmutability: false,
          strictStateSerializability: false,
        },
      }),
      StoreDevtoolsModule.instrument({ maxAge: 10 }),
      EffectsModule.forRoot([
        PrismicEffects,
        ProductEffects,
        ConfigEffects,
        CartEffects,
        UserEffects,
        VehicleEffects,
        RemitEffects,
        HistoryEffects,
        ClaimsEffects,
      ]),
      MatNativeDateModule,
      MatMomentDateModule
    ),
    CookieService,
    requestInterceptor,
    WindowRef,
    DocumentRef,
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
});
